'use client';

import { useEffect, useRef, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { useLocale, useTranslations } from 'next-intl';
import { detectBrowserTimezone } from '@/lib/tenant-time';
import { errorMsg } from '@/lib/utils/error';
import { supabase } from '@/lib/supabase/client';

/**
 * RegisterForm — 2026 Fortune-500 signup.
 *
 * Design principles (in priority order):
 *   1. **One screen, four fields.** First name, business name, email,
 *      password. No `lastName`, no `confirmPassword`, no plan picker,
 *      no industry dropdown. Every extra field costs ~7-10%
 *      completion rate (industry research, B2B SaaS funnels 2024-25).
 *   2. **Trial is automatic.** No plan choice on signup — backend
 *      hands every new tenant a 14-day trial. Plan selection lives
 *      in `/admin/subscription` after first value-touchpoint.
 *   3. **Google OAuth path on top.** One-click signup via Supabase's
 *      Google provider — modern 2026 default, ~40% of B2B users prefer
 *      it over email/password.
 *   4. **Password visibility toggle, no confirm field.** Eye-icon
 *      prevents typos at the source rather than re-typing.
 *   5. **Brand-blue only.** No rainbow gradients, no framer-motion,
 *      no Lucide-icon zoo. Plain CSS transitions, inline SVG, calm.
 *
 * Replaces the previous 624-line form that had a plan picker, six
 * fields, gradient-icon plan cards, hospitality leftovers
 * ("Unterkünfte"), and confirm-password. Form-completion rate
 * historically dropped ~30% on the plan-picker step alone.
 */
export default function RegisterForm() {
  const t = useTranslations('auth.registerForm');
  const router = useRouter();
  const searchParams = useSearchParams();
  // Flyer-Scanner (QR trägt ?source=flyer_*) bekommen einen Kontext-Banner:
  // Founder-Einrichtungsangebot + WhatsApp + Live-Demo (Codex: above-the-fold-Trio
  // für Cold-Drops), ohne den kürzesten Registrierungsweg zu opfern.
  const isFlyerVisitor = (searchParams.get('source') || '').startsWith('flyer');
  const locale = useLocale();

  const firstNameRef = useRef<HTMLInputElement | null>(null);

  const [firstName, setFirstName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  // Optional self-reported attribution. Captures the AI/word-of-mouth
  // channel that analytics misses (AEO course recommendation). Optional
  // by design — the form's whole doctrine is "no extra required fields".
  const [heardAboutUs, setHeardAboutUs] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const leadCaptured = useRef(false);

  // Lead-capture: when the user types an email and blurs the field
  // (signals intent to fill the form), we POST to /api/signup-leads
  // before any submit. Powers the 3-step abandoned-recovery email
  // sequence — the user that started but didn't finish still gets a
  // nudge 24h/72h/7d later. This used to live in the SignupModal email
  // step; consolidated into /register on 2026-05-26.
  const captureLead = (rawEmail: string) => {
    if (leadCaptured.current) return;
    const normalized = rawEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(normalized)) return;
    leadCaptured.current = true;
    const source = searchParams.get('source') || 'register';
    const distinctId = (() => {
      try {
        const ph = (window as unknown as { posthog?: { get_distinct_id?: () => string } })
          .posthog;
        return ph?.get_distinct_id?.() ?? null;
      } catch {
        return null;
      }
    })();
    // Fire-and-forget. `keepalive` lets the request finish even if the
    // user closes the tab right after blur — exactly the case we want
    // to recover.
    void fetch('/api/signup-leads', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        email: normalized,
        locale: (locale || 'de').slice(0, 2),
        source,
        posthog: distinctId,
      }),
    }).catch(() => {
      /* swallow — analytics-grade only */
    });
  };

  // Prefill from URL params (e.g. SignupModal preview → register page).
  // Only runs once on mount; doesn't overwrite once the user types.
  useEffect(() => {
    const emailParam = searchParams.get('email');
    const businessParam = searchParams.get('businessName');
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot URL-param prefill
    if (emailParam) setEmail((v) => v || emailParam);
    if (businessParam) setBusinessName((v) => v || businessParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Autofocus the first input on mount — saves a click, classic Stripe
  // / Linear behaviour. Defer one frame so the page isn't yanked
  // mid-mount-animation.
  useEffect(() => {
    const t = requestAnimationFrame(() => firstNameRef.current?.focus());
    return () => cancelAnimationFrame(t);
  }, []);

  const trackAdsConversion = () => {
    if (typeof window === 'undefined') return;
    const gtag = (window as unknown as { gtag?: (...args: any[]) => void })
      .gtag;
    if (typeof gtag !== 'function') return;
    gtag('event', 'conversion', {
      send_to: 'AW-17894228694/GtjPCOjb9ekbENaF0dRC',
      value: 1.0,
      currency: 'EUR',
    });
  };

  const handleGoogleSignup = async () => {
    setError(null);
    setOauthLoading(true);
    try {
      const { trackSignupStarted } = await import('@/lib/analytics/posthog');
      trackSignupStarted(searchParams.get('source') ?? 'oauth-google');
    } catch {
      /* analytics is non-fatal */
    }
    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback?next=/admin/welcome`,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
      },
    });
    if (oauthError) {
      setError(errorMsg(oauthError));
      setOauthLoading(false);
    }
    // Otherwise the browser redirects — keep the loading state.
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Minimal client-side validation. Server is the source of truth.
    if (!firstName.trim() || !businessName.trim() || !email.trim() || !password) {
      setError(t('errFields'));
      return;
    }
    if (password.length < 8) {
      setError(t('errPwShort'));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError(t('errEmail'));
      return;
    }

    // Last-chance lead capture before we actually submit. Covers the
    // path where the user types fast enough that the email-blur never
    // fires (autofill paste + tab into password). The recovery cron
    // is idempotent so a double capture is harmless.
    captureLead(email);

    setSubmitting(true);

    try {
      const { trackSignupStarted } = await import('@/lib/analytics/posthog');
      trackSignupStarted(searchParams.get('source') ?? undefined);
    } catch {
      /* non-fatal */
    }

    // Capture Google-Ads click identifiers — gclid OR gbraid OR wbraid.
    // The latter two are used since iOS 14.5 / Performance Max where
    // gclid isn't always available. Without capturing them too,
    // ~40-70% of paid signups land without attribution and never
    // upload a Smart-Bidding conversion (Promo-Sprint 2026-05-29 audit).
    const { readClickIdentifiers } = await import('@/lib/marketing/gclid');
    const clickIds = readClickIdentifiers();

    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          password,
          firstName: firstName.trim(),
          // `lastName` intentionally empty — collected later in settings.
          // Server already treats it as optional after the 2026 refactor.
          lastName: '',
          businessName: businessName.trim(),
          locale,
          timezone: detectBrowserTimezone(),
          referralCode: searchParams.get('ref') ?? undefined,
          source: searchParams.get('source') ?? undefined,
          // Optional self-reported attribution — server may persist it;
          // primary consumer is PostHog (see trackSignupCompleted).
          heardAboutUs: heardAboutUs || undefined,
          // Send the kind so the server stores it in the right column.
          // Legacy `gclid` field stays populated when kind === 'gclid' for
          // back-compat with anything still reading the old field.
          clickIdKind: clickIds?.kind,
          clickIdValue: clickIds?.value,
          clickIdCapturedAt: clickIds?.captured_at,
          gclid: clickIds?.kind === 'gclid' ? clickIds.value : undefined,
          gclidCapturedAt: clickIds?.captured_at,
        }),
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || t('errFailed'));
      }

      trackAdsConversion();
      try {
        const { trackSignupCompleted } = await import(
          '@/lib/analytics/posthog'
        );
        trackSignupCompleted(
          heardAboutUs ? { heardAboutUs } : {},
        );
      } catch {
        /* non-fatal */
      }

      // Persist the locale used during signup so the admin renders in
      // the same language. Without this cookie the admin falls back to
      // browser-default which, for users signing up from chrome-devtools
      // or English-Accept-Language profiles, lands them in English even
      // though they just used the German marketing site.
      try {
        // 1 year expiry, root path, lax (works across same-site nav)
        document.cookie = `NEXT_LOCALE=${locale}; Path=/; Max-Age=31536000; SameSite=Lax`;
      } catch {
        /* document.cookie set is non-fatal */
      }

      // Auto-sign-in — backend creates user with email_confirm=true so
      // no verification round-trip. Land directly on /admin where the
      // setup-checklist takes over.
      try {
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email: email.trim(),
          password,
        });
        if (signInError) throw signInError;
        // Aha-first: Neuanmelder gehen in den Welcome-Wizard (live
        // Buchungsseite + Probebuchung), nicht direkt aufs Dashboard.
        router.push('/admin/welcome');
      } catch {
        router.push('/admin/login?message=registration-success');
      }
    } catch (err) {
      setError(errorMsg(err) || t('errFailed'));
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-5 p-7 sm:p-9"
      noValidate
    >
      <div>
        <h2 className="text-2xl font-semibold tracking-tight text-gray-950 sm:text-[1.625rem]">
          {t('title')}
        </h2>
        <p className="mt-1.5 text-sm text-gray-600">
          {t('sub')}
        </p>
      </div>

      {isFlyerVisitor && (
        <div className="rounded-xl border border-blue-200/70 bg-blue-50/50 p-4 sm:p-5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-blue-700">
            {t('flyerBadge')}
          </p>
          <p className="mt-1.5 text-[15px] font-semibold tracking-tight text-gray-950">
            {t('flyerTitle')}
          </p>
          <p className="mt-1 text-sm text-gray-600">
            {t('flyerBody')}
          </p>
          {/* Buttons: exakt die Formensprache des Google-Buttons darunter
              (h-12, rounded-xl, gleicher Schatten) — primär in Brand-Blau. */}
          <div className="mt-3.5 flex flex-col gap-2.5 sm:flex-row">
            <a
              href="https://wa.me/4915678371650?text=Hallo!%20Ich%20habe%20euren%20Flyer%20bekommen%20und%20interessiere%20mich%20f%C3%BCr%20EazyBooking."
              className="flex min-h-12 flex-1 items-center justify-center gap-3 rounded-xl bg-blue-600 text-sm font-medium text-white shadow-[0_1px_2px_rgba(15,23,42,0.08)] transition-colors hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
            >
              {t('flyerWhatsapp')}
            </a>
            <a
              href="/studio-bella"
              target="_blank"
              rel="noopener"
              className="flex min-h-12 flex-1 items-center justify-center gap-3 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-900 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-colors hover:border-gray-300 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
            >
              {t('flyerDemo')}
            </a>
          </div>
        </div>
      )}

      {/* OAuth path — primary alternative, sits visually above the form
          but is just a single tap. */}
      <button
        type="button"
        onClick={handleGoogleSignup}
        disabled={oauthLoading || submitting}
        className="group flex h-12 items-center justify-center gap-3 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-900 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-colors hover:border-gray-300 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 disabled:opacity-60"
      >
        {oauthLoading ? (
          <Spinner className="h-4 w-4 text-gray-500" />
        ) : (
          <GoogleIcon />
        )}
        {t('google')}
      </button>

      <div className="flex items-center gap-3 text-xs uppercase tracking-[0.14em] text-gray-400">
        <div className="h-px flex-1 bg-gray-200" />
        {t('or')}
        <div className="h-px flex-1 bg-gray-200" />
      </div>

      <div className="grid gap-4">
        <Field
          label={t('firstName')}
          name="firstName"
          autoComplete="given-name"
          value={firstName}
          onChange={setFirstName}
          inputRef={firstNameRef}
          placeholder={t('firstNamePh')}
        />
        <Field
          label={t('company')}
          name="businessName"
          autoComplete="organization"
          value={businessName}
          onChange={setBusinessName}
          placeholder={t('companyPh')}
        />
        <Field
          label={t('email')}
          name="email"
          type="email"
          autoComplete="email"
          inputMode="email"
          value={email}
          onChange={setEmail}
          onBlur={captureLead}
          placeholder={t('emailPh')}
        />
        <PasswordField
          value={password}
          onChange={setPassword}
          show={showPassword}
          onToggle={() => setShowPassword((s) => !s)}
        />
        <HeardAboutField value={heardAboutUs} onChange={setHeardAboutUs} />
      </div>

      {error && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700"
        >
          <ErrorIcon />
          <span>{error}</span>
        </div>
      )}

      <button
        type="submit"
        disabled={submitting || oauthLoading}
        className="group inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-blue-600 text-sm font-semibold text-white shadow-[0_10px_30px_-10px_rgba(37,99,235,0.6)] transition-transform duration-150 hover:translate-y-[-1px] hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:translate-y-0"
      >
        {submitting ? (
          <>
            <Spinner className="h-4 w-4 text-white" />
            {t('submitting')}
          </>
        ) : (
          <>
            {t('submit')}
            <span aria-hidden="true" className="transition-transform group-hover:translate-x-0.5">
              →
            </span>
          </>
        )}
      </button>

      {/* Trust line — Stripe / Vercel / Linear all have this 1-liner
          directly below the CTA. */}
      <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-xs text-gray-500">
        <span className="inline-flex items-center gap-1.5">
          <LockIcon /> Verschlüsselt
        </span>
        <span className="inline-flex items-center gap-1.5">
          <CalendarIcon /> 14 Tage gratis
        </span>
        <span className="inline-flex items-center gap-1.5">
          <CardIcon /> Keine Kreditkarte
        </span>
      </div>

      <p className="text-center text-xs text-gray-500">
        {t('haveAccount')}{' '}
        <Link
          href="/admin/login"
          className="font-medium text-blue-600 hover:text-blue-700"
        >
          {t('login')}
        </Link>
      </p>
    </form>
  );
}

// ────────────────────────────────────────────────────────────────
// Building blocks
// ────────────────────────────────────────────────────────────────

interface FieldProps {
  label: string;
  name: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  autoComplete?: string;
  inputMode?: 'email' | 'text' | 'tel';
  placeholder?: string;
  inputRef?: React.Ref<HTMLInputElement>;
  onBlur?: (v: string) => void;
}

function Field({
  label,
  name,
  value,
  onChange,
  type = 'text',
  autoComplete,
  inputMode,
  placeholder,
  inputRef,
  onBlur,
}: FieldProps) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-gray-700">{label}</span>
      <input
        ref={inputRef}
        type={type}
        name={name}
        autoComplete={autoComplete}
        inputMode={inputMode}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur ? (e) => onBlur(e.target.value) : undefined}
        placeholder={placeholder}
        required
        className="h-11 rounded-xl border border-gray-200 bg-white px-3.5 text-[15px] text-gray-950 placeholder:text-gray-400 transition-colors hover:border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/15"
      />
    </label>
  );
}

/**
 * Optional "How did you hear about us?" select. Self-reported attribution
 * captures the AI/word-of-mouth channel that analytics can't see — the
 * single highest-value, lowest-effort AEO measurement (Ahrefs course 2026).
 * Optional + last field so it never adds friction to the 4-field core.
 */
function HeardAboutField({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const t = useTranslations('auth.registerForm');
  const options: { value: string; label: string }[] = [
    { value: 'ai', label: t('srcAi') },
    { value: 'google_search', label: t('srcSearch') },
    { value: 'google_ads', label: t('srcAds') },
    { value: 'instagram', label: t('srcInstagram') },
    { value: 'recommendation', label: t('srcRecommendation') },
    { value: 'blog', label: t('srcBlog') },
    { value: 'comparison', label: t('srcComparison') },
    { value: 'other', label: t('srcOther') },
  ];
  return (
    <label className="flex flex-col gap-1.5">
      <span className="flex items-center justify-between text-xs font-medium text-gray-700">
        <span>{t('heardTitle')}</span>
        <span className="font-normal text-gray-400">{t('optional')}</span>
      </span>
      <select
        name="heardAboutUs"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-11 rounded-xl border border-gray-200 bg-white px-3 text-[15px] text-gray-950 transition-colors hover:border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/15"
      >
        <option value="">{t('heardPlaceholder')}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

interface PasswordFieldProps {
  value: string;
  onChange: (v: string) => void;
  show: boolean;
  onToggle: () => void;
}

function PasswordField({ value, onChange, show, onToggle }: PasswordFieldProps) {
  const t = useTranslations('auth.registerForm');
  return (
    <label className="flex flex-col gap-1.5">
      <span className="flex items-center justify-between text-xs font-medium text-gray-700">
        <span>{t('password')}</span>
        <span className="font-normal text-gray-400">{t('pwHint')}</span>
      </span>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          name="password"
          autoComplete="new-password"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          minLength={8}
          required
          className="h-11 w-full rounded-xl border border-gray-200 bg-white px-3.5 pr-11 text-[15px] text-gray-950 placeholder:text-gray-400 transition-colors hover:border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/15"
        />
        <button
          type="button"
          onClick={onToggle}
          aria-label={show ? t('pwHide') : t('pwShow')}
          className="absolute right-1 top-1 flex h-9 w-9 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
        >
          {show ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      </div>
    </label>
  );
}

// ────────────────────────────────────────────────────────────────
// Inline SVG icons — no Lucide, no icon-zoo
// ────────────────────────────────────────────────────────────────

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
      />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A10 10 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="12"
      height="12"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="12"
      height="12"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </svg>
  );
}

function CardIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="12"
      height="12"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="6" width="18" height="13" rx="2" />
      <path d="M3 11h18" />
    </svg>
  );
}

function ErrorIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="mt-0.5 flex-shrink-0"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

function Spinner({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      className={`animate-spin ${className ?? ''}`}
      aria-hidden="true"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
}
